import { assoc, assocPath } from 'ramda';

import { toCamelCase } from '@/lib/utils';
import { type QuestionSchema } from '@/lib/utils/form-validations/forms/nurturing-quiz-form/api';
import { type SelectedCourseAnswer } from '../../icon-question';
import {
  type ImageAnswer,
  type ImageAnswerCode,
} from '../../image-question/image-answer';

type Answer =
  | Pick<SelectedCourseAnswer, 'code' | 'questionCode'>
  | Pick<ImageAnswer, 'code'>;

const genderCodeToSalesforceMap: Partial<
  Record<ImageAnswerCode, QuestionSchema['gender']>
> = {
  female: 'Female',
  male: 'Male',
  'non-binary': 'Non-binary',
  'not-disclose': 'Prefer not to disclose',
};

const ageCodeToSalesforceMap: Partial<
  Record<ImageAnswerCode, QuestionSchema['ageRange']>
> = {
  '17-below': '17-',
  '18-24': '18-24',
  '25-34': '25-34',
  '35-44': '35-44',
  '45-above': '45+',
};

const techBackgroundCodeToSalesforceMap: Partial<
  Record<string, QuestionSchema['techBackground']>
> = {
  advanced: 'Advanced',
  'beginning-learner': 'Beginning learner',
  'intermediate-learner': 'Intermediate learner',
  'no-experience': 'No experience',
  professional: 'Professional',
};

export const getSalesforceQuestionStructure = (
  answers: Answer[]
): Partial<QuestionSchema> => {
  const salesforceQuestionStructure = answers.reduce(
    (acc: Partial<QuestionSchema>, answer: Answer) => {
      let salesforceValue;

      if ('questionCode' in answer) {
        const { code, questionCode } = answer;
        salesforceValue = techBackgroundCodeToSalesforceMap[code];
        if (salesforceValue) {
          return assoc('techBackground', salesforceValue, acc);
        }
        const camelQuestion = toCamelCase(questionCode);
        const camelAnswer = toCamelCase(code);
        return assocPath(['otherFields', camelQuestion], camelAnswer, acc);
      }

      const { code } = answer;
      salesforceValue = genderCodeToSalesforceMap[code];
      if (salesforceValue) {
        return assoc('gender', salesforceValue, acc);
      }

      salesforceValue = ageCodeToSalesforceMap[code];
      if (salesforceValue) {
        return assoc('ageRange', salesforceValue, acc);
      }
      return acc;
    },
    { otherFields: {} }
  );

  return salesforceQuestionStructure;
};
